/**
 * PdfViewer Component
 *
 * Displays PDF files using PDF.js with page navigation and zoom controls.
 * Supports zoom, fit-to-width, fit-to-page, and keyboard shortcuts.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { downloadWorkspaceFile } from '@/lib/downloadFile';
import { api } from '@/services/api';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  AlignHorizontalJustifyCenter,
  Download,
  Loader2,
  AlertCircle,
  FileText,
} from 'lucide-react';

// Configure PDF.js worker
// Use CDN worker to avoid bundling issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

// ============================================
// Types
// ============================================

interface PdfViewerProps {
  /** Path to the PDF file */
  path: string;
  /** File name (for display) */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

interface PdfState {
  isLoading: boolean;
  error: string | null;
  numPages: number;
  currentPage: number;
}

type FitMode = 'custom' | 'width' | 'page';

// ============================================
// Constants
// ============================================

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const DEFAULT_ZOOM = 1;

// ============================================
// PdfViewer Component
// ============================================

export function PdfViewer({ path, filename, className }: PdfViewerProps) {
  // PDF state
  const [pdfState, setPdfState] = useState<PdfState>({
    isLoading: true,
    error: null,
    numPages: 0,
    currentPage: 1,
  });

  // Zoom state
  const [scale, setScale] = useState(DEFAULT_ZOOM);
  const [fitMode, setFitMode] = useState<FitMode>('width');

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);
  const pageRef = useRef<pdfjsLib.PDFPageProxy | null>(null);

  // ----------------------------------------
  // PDF Loading
  // ----------------------------------------

  const loadPdf = useCallback(async () => {
    setPdfState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Get the PDF URL
      const pdfUrl = api.files.getDownloadUrl(path, true);

      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl,
        withCredentials: true,
      });

      const pdfDoc = await loadingTask.promise;
      pdfDocRef.current = pdfDoc;

      setPdfState({
        isLoading: false,
        error: null,
        numPages: pdfDoc.numPages,
        currentPage: 1,
      });
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setPdfState({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load PDF',
        numPages: 0,
        currentPage: 1,
      });
    }
  }, [path]);

  // Load PDF on mount or path change
  useEffect(() => {
    loadPdf();

    return () => {
      // Cleanup
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
      if (pageRef.current) {
        pageRef.current.cleanup();
        pageRef.current = null;
      }
    };
  }, [loadPdf]);

  // ----------------------------------------
  // Page Rendering
  // ----------------------------------------

  const renderPage = useCallback(async () => {
    if (!pdfDocRef.current || !canvasRef.current || !containerRef.current) return;

    const pdfDoc = pdfDocRef.current;
    const currentPage = pdfState.currentPage;

    if (currentPage < 1 || currentPage > pdfDoc.numPages) return;

    // Cancel any existing render task
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    // Cleanup previous page
    if (pageRef.current) {
      pageRef.current.cleanup();
      pageRef.current = null;
    }

    try {
      // Get the page
      const page = await pdfDoc.getPage(currentPage);
      pageRef.current = page;

      // Get viewport
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 48; // Padding
      const containerHeight = container.clientHeight - 48;

      // Calculate scale based on fit mode
      const defaultViewport = page.getViewport({ scale: 1 });
      let computedScale = scale;

      if (fitMode === 'width') {
        computedScale = containerWidth / defaultViewport.width;
      } else if (fitMode === 'page') {
        const scaleX = containerWidth / defaultViewport.width;
        const scaleY = containerHeight / defaultViewport.height;
        computedScale = Math.min(scaleX, scaleY);
      }

      // Update scale if fit mode changed it
      if (fitMode !== 'custom' && computedScale !== scale) {
        setScale(computedScale);
      }

      const viewport = page.getViewport({ scale: computedScale });

      // Setup canvas
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) return;

      // Handle high DPI displays
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      context.scale(dpr, dpr);

      // Render the page
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
      renderTaskRef.current = null;
    } catch (err) {
      // Ignore cancelled render tasks
      if (err instanceof Error && err.name === 'RenderingCancelledException') {
        return;
      }
      console.error('Failed to render page:', err);
    }
  }, [pdfState.currentPage, scale, fitMode]);

  // Render page when state changes
  useEffect(() => {
    if (!pdfState.isLoading && !pdfState.error && pdfState.numPages > 0) {
      renderPage();
    }
  }, [renderPage, pdfState.isLoading, pdfState.error, pdfState.numPages]);

  // Handle container resize
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (fitMode !== 'custom') {
        renderPage();
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [fitMode, renderPage]);

  // ----------------------------------------
  // Navigation Controls
  // ----------------------------------------

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > pdfState.numPages) return;
      setPdfState((prev) => ({ ...prev, currentPage: page }));
    },
    [pdfState.numPages]
  );

  const previousPage = useCallback(() => {
    goToPage(pdfState.currentPage - 1);
  }, [goToPage, pdfState.currentPage]);

  const nextPage = useCallback(() => {
    goToPage(pdfState.currentPage + 1);
  }, [goToPage, pdfState.currentPage]);

  // ----------------------------------------
  // Zoom Controls
  // ----------------------------------------

  const zoomIn = useCallback(() => {
    setFitMode('custom');
    setScale((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setFitMode('custom');
    setScale((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const fitToWidth = useCallback(() => {
    setFitMode('width');
  }, []);

  const fitToPage = useCallback(() => {
    setFitMode('page');
  }, []);

  // ----------------------------------------
  // Download
  // ----------------------------------------

  const handleDownload = useCallback(() => {
    void downloadWorkspaceFile(path, filename);
  }, [filename, path]);

  // ----------------------------------------
  // Keyboard Shortcuts
  // ----------------------------------------

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if this component is focused
      if (!containerRef.current?.contains(document.activeElement)) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          previousPage();
          break;
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          nextPage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          zoomIn();
          break;
        case '-':
          e.preventDefault();
          zoomOut();
          break;
        case 'Home':
          e.preventDefault();
          goToPage(1);
          break;
        case 'End':
          e.preventDefault();
          goToPage(pdfState.numPages);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previousPage, nextPage, zoomIn, zoomOut, goToPage, pdfState.numPages]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  const zoomPercent = Math.round(scale * 100);

  return (
    <div
      className={cn(
        'h-full w-full flex flex-col',
        'bg-surface-950 dark:bg-surface-950',
        className
      )}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-surface-800 bg-surface-900">
        {/* Left: File info */}
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-surface-400" />
          <span className="text-sm text-surface-300 font-medium truncate max-w-[200px]">
            {filename}
          </span>
          {pdfState.numPages > 0 && (
            <span className="text-xs text-surface-500">
              {pdfState.numPages} {pdfState.numPages === 1 ? 'page' : 'pages'}
            </span>
          )}
        </div>

        {/* Center: Page navigation and zoom */}
        <div className="flex items-center gap-4">
          {/* Page navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={previousPage}
              disabled={pdfState.currentPage <= 1 || pdfState.isLoading}
              title="Previous page (Left Arrow)"
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <span className="text-xs text-surface-400 w-20 text-center tabular-nums">
              {pdfState.currentPage} / {pdfState.numPages || '-'}
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={nextPage}
              disabled={pdfState.currentPage >= pdfState.numPages || pdfState.isLoading}
              title="Next page (Right Arrow)"
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="w-px h-4 bg-surface-700" />

          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomOut}
              disabled={scale <= MIN_ZOOM || pdfState.isLoading}
              title="Zoom out (-)"
              className="h-8 w-8"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>

            <span className="text-xs text-surface-400 w-12 text-center tabular-nums">
              {zoomPercent}%
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={zoomIn}
              disabled={scale >= MAX_ZOOM || pdfState.isLoading}
              title="Zoom in (+)"
              className="h-8 w-8"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>

            <div className="w-px h-4 bg-surface-700 mx-1" />

            <Button
              variant="ghost"
              size="icon"
              onClick={fitToWidth}
              title="Fit to width"
              className={cn('h-8 w-8', fitMode === 'width' && 'bg-surface-800')}
              disabled={pdfState.isLoading}
            >
              <AlignHorizontalJustifyCenter className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={fitToPage}
              title="Fit to page"
              className={cn('h-8 w-8', fitMode === 'page' && 'bg-surface-800')}
              disabled={pdfState.isLoading}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            title="Download"
            className="h-8"
            disabled={pdfState.isLoading}
          >
            <Download className="h-4 w-4 mr-1" />
            <span className="text-xs">Download</span>
          </Button>
        </div>
      </div>

      {/* PDF container */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-auto relative',
          'flex justify-center',
          'bg-surface-800'
        )}
        tabIndex={0}
      >
        {/* Loading state */}
        {pdfState.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-900/80">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-surface-400">Loading PDF...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {pdfState.error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-destructive-900/30 text-destructive-400 mb-4">
                <AlertCircle className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-semibold text-surface-100 mb-2">
                Failed to load PDF
              </h2>
              <p className="text-surface-400 mb-4">{pdfState.error}</p>
              <Button variant="outline" onClick={loadPdf}>
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* PDF canvas */}
        {!pdfState.isLoading && !pdfState.error && (
          <div className="p-6">
            <canvas
              ref={canvasRef}
              className={cn(
                'shadow-lg',
                'bg-white'
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default PdfViewer;
